import * as React from "react";
import { Check, ImageIcon } from "lucide-react";
import { Button, Spinner } from "@/components/ui";
import { HoloButton, HoloDot, SocoAiLogo } from "@/components/Holo";
import { formatRelativeTime } from "@/components/reviews";
import { cn } from "@/lib/utils";
import { ActorRef, DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { SlideBusy } from "../slideState";
import { ACTIVITY_LABELS, asUser, RequestOutcome, RequestScope, RequestSource, ThreadEntry } from "../thread";
import { ActorAvatar } from "./ActivityPanel";
import { confirmKeep } from "./CandidateCompare";
import { DisabledReason } from "./DisabledReason";
import { ImageDialog } from "./ImageDownload";
import { ASPECT_CLASS } from "./PackageCard";
import { SupportLine } from "../help/SupportLine";
import { designFileName } from "../download";

/** A request just sent, shown until its activity event arrives. */
export interface PendingRequest {
  instructions: string;
  scope: RequestScope;
  actor: ActorRef;
  at: string;
}

/** Keep and Discard for a slide's pending version, right under the request that made it. */
export interface ThreadDecisions {
  canDecide: boolean;
  /** Why Keep and Discard are unavailable (another refinement is running). */
  disabledReason: string | null;
  busy: SlideBusy;
  onKeep(itemId: number): void;
  onDiscard(itemId: number): void;
}

interface RefinementThreadProps {
  entries: ThreadEntry[];
  pkg: DesignPackageDetail;
  /** Requests about this slide are highlighted; others keep a tag that jumps to their slide. */
  selectedItemId?: number;
  onSelectItem: (id: number) => void;
  onPrefill: (instructions: string) => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  pending?: PendingRequest | null;
  decisions?: ThreadDecisions;
}

// Within this distance of the bottom, a new entry scrolls into view.
const STICK_TO_BOTTOM_PX = 80;
export const HTML_EDIT_LEAD = "Updated the design's code";

const MetaLine: React.FC<{ actor: ActorRef | null; at: string; children?: React.ReactNode }> = ({
  actor,
  at,
  children,
}) => (
  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500">
    {actor && <ActorAvatar actor={actor} />}
    {actor && (
      <span className="font-semibold text-slate-700">
        {actor.name}
        {actor.onBehalfOf && <span className="font-normal text-slate-500"> {asUser(actor.onBehalfOf.name)}</span>}
      </span>
    )}
    <span aria-hidden="true">·</span>
    <span>{formatRelativeTime(at)}</span>
    {children}
  </div>
);

const PILL =
  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold";

/** Where a change stands, as a pill in the corner of its card. */
const OutcomePill: React.FC<{ outcome: RequestOutcome }> = ({ outcome }) => {
  switch (outcome) {
    case "rendering":
      return (
        <span className={cn(PILL, "bg-indigo-50 text-primary")}>
          <Spinner className="size-3" /> Working…
        </span>
      );
    case "ready":
      return (
        <span className={cn(PILL, "bg-white text-primary ring-1 ring-slate-200")}>
          <HoloDot className="size-1.5" /> New version ready
        </span>
      );
    case "kept":
      return (
        <span className={cn(PILL, "bg-emerald-50 text-emerald-700")}>
          <Check className="size-3" aria-hidden="true" /> Kept
        </span>
      );
    case "discarded":
      return <span className={cn(PILL, "bg-slate-200/70 text-slate-500")}>Discarded</span>;
    case "failed":
      return <span className={cn(PILL, "bg-red-50 text-destructive")}>Couldn't apply</span>;
  }
};

const ScopeTag: React.FC<{
  scope: RequestScope;
  multiSlide: boolean;
  current: boolean;
  onSelectItem(id: number): void;
}> = ({ scope, multiSlide, current, onSelectItem }) => {
  if (!multiSlide) return null;
  if (scope === "all") return <span className="text-slate-500">· All slides</span>;
  if (current) return <span className="font-semibold text-primary">· Slide {scope.position}</span>;
  return (
    <>
      <span aria-hidden="true">·</span>
      <Button
        variant="link"
        size="inline"
        className="text-xs text-slate-500"
        onClick={() => onSelectItem(scope.itemId)}
      >
        Slide {scope.position}
      </Button>
    </>
  );
};

const OriginalRequest: React.FC<{ entry: Extract<ThreadEntry, { kind: "generated" }> }> = ({ entry }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-3" data-testid="thread-original">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">Original request</span>
        <SocoAiLogo variant="mono" height={11} />
        {entry.actor && <span>· {entry.actor.name}</span>}
        <span>· {formatRelativeTime(entry.at)}</span>
      </div>
      <p className={cn("text-sm text-slate-600", !expanded && "line-clamp-2")}>{entry.instructions}</p>
      <Button variant="link" size="inline" className="self-start text-xs" onClick={() => setExpanded(!expanded)}>
        {expanded ? "Show less" : "Show more"}
      </Button>
    </div>
  );
};

export const RefinementThread: React.FC<RefinementThreadProps> = ({
  entries,
  pkg,
  selectedItemId,
  onSelectItem,
  onPrefill,
  isLoadingMore,
  hasMore,
  onLoadMore,
  pending,
  decisions,
}) => {
  const endRef = React.useRef<HTMLDivElement>(null);
  const lastId = pending ? "pending" : entries[entries.length - 1]?.id;
  const nearBottom = React.useRef(true);
  const multiSlide = pkg.items.length > 1;
  const [openImage, setOpenImage] = React.useState<{ url: string; title: string; fileName: string } | null>(null);

  // The right pane is the scroll container; remember whether the reader was near the bottom
  // before the new entry rendered.
  React.useEffect(() => {
    const scroller = endRef.current?.closest("[data-thread-scroll]");
    if (!scroller) return;
    const onScroll = () => {
      nearBottom.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= STICK_TO_BOTTOM_PX;
    };
    onScroll();
    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    if (lastId != null && nearBottom.current) endRef.current?.scrollIntoView?.({ block: "end" });
  }, [lastId]);

  // Only the newest request behind a slide's pending version offers Keep and Discard.
  const decidable = new Map<number, number>();
  entries.forEach(entry => {
    if (entry.kind !== "request" || entry.scope === "all" || entry.outcome !== "ready") return;
    const item = pkg.items.find(i => i.id === (entry.scope as { itemId: number }).itemId);
    const thumb = entry.thumbnails[0];
    if (item?.candidateTemplate && thumb?.templateId === item.candidateTemplate.id) decidable.set(item.id, entry.id);
  });

  const keep = async (itemId: number) => {
    if (decisions && (await confirmKeep(!!pkg.publishedAt))) decisions.onKeep(itemId);
  };

  const renderRequest = (
    key: React.Key,
    request: {
      actor: ActorRef;
      at: string;
      instructions: string;
      source?: RequestSource;
      scope: RequestScope;
      outcome: RequestOutcome;
      thumbnails: { itemId: number; templateId: number | null; url: string | null }[];
      entryId?: number;
    }
  ) => {
    const { scope } = request;
    const htmlEdit = request.source === "html_edit";
    // An admin's edit leads with their note when they left one; a refinement always has its text.
    const lead = htmlEdit && !request.instructions ? HTML_EDIT_LEAD : request.instructions;
    const current = multiSlide && scope !== "all" && scope.itemId === selectedItemId;
    const decideItem =
      request.entryId != null && scope !== "all" && decidable.get(scope.itemId) === request.entryId
        ? scope.itemId
        : null;
    const itemBusy = decideItem != null ? decisions?.busy[decideItem] : undefined;

    return (
      <div
        key={key}
        className={cn(
          "flex flex-col gap-2 rounded-xl bg-slate-50 p-3",
          // Changes to the slide on screen stand out from the rest.
          current && "ring-1 ring-inset ring-primary/40"
        )}
        data-testid="thread-request"
        data-request-source={request.source ?? "refine"}
        data-current-slide={current || undefined}
      >
        {htmlEdit && (
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {ACTIVITY_LABELS.html_edited}
          </div>
        )}
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm font-medium text-slate-900">{lead}</p>
          <OutcomePill outcome={request.outcome} />
        </div>
        <MetaLine actor={request.actor} at={request.at}>
          <ScopeTag scope={scope} multiSlide={multiSlide} current={current} onSelectItem={onSelectItem} />
        </MetaLine>
        {(request.thumbnails.length > 0 || request.outcome === "failed") && (
          <div className="flex flex-wrap items-center gap-2">
            {request.thumbnails.map(thumb => {
              const position = pkg.items.find(i => i.id === thumb.itemId)?.position;
              const { url } = thumb;
              return (
                <button
                  key={`${thumb.itemId}:${thumb.templateId}`}
                  type="button"
                  // An image opens large; one that never rendered can only take the reader to its slide.
                  onClick={() =>
                    url
                      ? setOpenImage({
                          url,
                          title: multiSlide && position != null ? `Slide ${position}` : pkg.name,
                          fileName: designFileName(pkg.name, multiSlide ? position : undefined),
                        })
                      : onSelectItem(thumb.itemId)
                  }
                  className={cn(
                    "w-10 overflow-hidden rounded-md border border-slate-200 bg-slate-100 cursor-pointer",
                    ASPECT_CLASS[pkg.aspectRatio]
                  )}
                  aria-label={url ? `View slide ${position ?? ""}` : `Show slide ${position ?? ""}`}
                >
                  {thumb.url ? (
                    <img src={thumb.url} alt="" className="size-full object-cover" />
                  ) : (
                    <span className="flex size-full items-center justify-center text-slate-300">
                      <ImageIcon className="size-3.5" aria-hidden="true" />
                    </span>
                  )}
                </button>
              );
            })}
            {request.outcome === "failed" && !htmlEdit && (
              <Button variant="link" size="inline" className="text-xs" onClick={() => onPrefill(request.instructions)}>
                Try again
              </Button>
            )}
          </div>
        )}
        {decideItem != null && decisions?.canDecide && (
          <DisabledReason reason={decisions.disabledReason} data-testid="thread-decision">
            <div className="flex flex-wrap gap-2">
              <HoloButton
                size="sm"
                onClick={() => void keep(decideItem)}
                disabled={!!decisions.disabledReason || itemBusy != null}
                loading={itemBusy === "approving"}
              >
                Use new version
              </HoloButton>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decisions.onDiscard(decideItem)}
                disabled={!!decisions.disabledReason || itemBusy != null}
                loading={itemBusy === "discarding"}
              >
                Keep current
              </Button>
            </div>
          </DisabledReason>
        )}
      </div>
    );
  };

  const hasRequests = !!pending || entries.some(entry => entry.kind === "request");

  return (
    <div className="flex flex-col gap-3" data-testid="refinement-thread">
      {hasMore && (
        <Button variant="ghost" size="sm" className="self-center" loading={isLoadingMore} onClick={onLoadMore}>
          Load earlier changes
        </Button>
      )}

      {entries.map(entry => {
        switch (entry.kind) {
          case "generated":
            return <OriginalRequest key={entry.id} entry={entry} />;
          case "system":
            return (
              <div key={entry.id} className="flex items-center gap-2 text-xs text-slate-400" data-testid="thread-event">
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
                <span className="text-center">
                  {entry.actor.name} · {entry.text} · {formatRelativeTime(entry.at)}
                </span>
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
              </div>
            );
          case "request":
            return renderRequest(entry.id, { ...entry, entryId: entry.id });
        }
      })}

      {pending && renderRequest("pending", { ...pending, outcome: "rendering", thumbnails: [] })}

      {!hasRequests && (
        <div
          className="flex flex-col gap-1 rounded-xl border border-dashed border-slate-200 p-4"
          data-testid="thread-empty"
        >
          <p className="text-sm font-semibold text-slate-800">Make it yours</p>
          <p className="text-sm text-slate-600">Describe a change below in plain words and we'll update the design.</p>
          {/* Always our own support: only our team can hand-fix a design's HTML */}
          <SupportLine className="text-slate-500" email="support@socialcoach.io" />
        </div>
      )}
      <div ref={endRef} />
      <ImageDialog image={openImage} onClose={() => setOpenImage(null)} />
    </div>
  );
};
